import { addMonths } from 'date-fns/addMonths'
import { endOfMonth } from 'date-fns/endOfMonth'
import { format } from 'date-fns/format'
import { getDay } from 'date-fns/getDay'
import { getDaysInMonth } from 'date-fns/getDaysInMonth'
import { startOfMonth } from 'date-fns/startOfMonth'
import { useMemo } from 'react'

import { Maybe } from 'aem-libs-ts/src/types/utility'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'
import { formatDateISO } from 'aem-libs-ts/src/utils/formatting/dates/formatDateISO'
import { range } from 'aem-libs-ts/src/utils/number/range'

import { DisableDatesFn } from './types'

interface DateWeekItemType {
  date: number
  selectable: boolean
  disabled: boolean
  dataDate: string
}

type DateWeeksType = [DateWeekItemType[]]

export const useDatesTransform = (date: Date, disableDates: Maybe<DisableDatesFn>): DateWeeksType => {
  const getCurrentMonthDays = (currentDate: Date): DateWeekItemType[] => {
    return pipe(() => currentDate)
      .map((date) => range(1, getDaysInMonth(date)))
      .map((days) =>
        days.map((day) => ({
          date: day,
          selectable: true,
          disabled: disableDates
            ? disableDates(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))
            : false,
          dataDate: format(new Date(currentDate.getFullYear(), currentDate.getMonth(), day), 'yyyy-MM-dd')
        }))
      )
      .get()
  }

  const getDaysBefore = (currentDate: Date): DateWeekItemType[] => {
    const prevMonth = addMonths(currentDate, -1)
    return pipe(() => currentDate)
      .map(startOfMonth)
      .map(getDay)
      .map((startDay) => (startDay === 0 ? 7 : startDay))
      .map((startDay) => ({
        startDay,
        daysInLastMonth: range(1, getDaysInMonth(prevMonth))
      }))
      .map((dataObj) =>
        dataObj.daysInLastMonth.slice(
          dataObj.daysInLastMonth.length - dataObj.startDay + 1,
          dataObj.daysInLastMonth.length
        )
      )
      .map((days) =>
        days.map((d) => ({
          date: d,
          selectable: false,
          disabled: false,
          dataDate: formatDateISO(new Date(prevMonth.getFullYear(), prevMonth.getMonth(), d))
        }))
      )
      .get()
  }

  const getDaysAfter = (currentDate: Date): DateWeekItemType[] => {
    const nextMonth = addMonths(currentDate, 1)
    return pipe(() => currentDate)
      .map(endOfMonth)
      .map(getDay)
      .throwWhen(
        (dayIndex) => dayIndex === 0,
        () => [] as DateWeekItemType[]
      )
      .map((endDayIndex) => Array.from({ length: 7 - endDayIndex }, (k, i) => i + 1))
      .map((days) =>
        days.map((d) => ({
          date: d,
          selectable: false,
          disabled: false,
          dataDate: formatDateISO(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), d))
        }))
      )
      .getOrElse((days) => days)
  }

  return useMemo<DateWeeksType>(() => {
    return pipe(() => date)
      .map((d) => ({
        before: getDaysBefore(d),
        current: getCurrentMonthDays(d),
        after: getDaysAfter(d)
      }))
      .map(({ before, current, after }) => [...before, ...current, ...after])
      .map((days) =>
        days.reduce((acc, curr, index) => {
          if (index === 0 || index % 7 === 0) {
            acc.push([])
          }
          const weekDays = acc[acc.length - 1] ?? []
          weekDays.push(curr)
          acc[acc.length - 1] = weekDays
          return acc
        }, [] as unknown as DateWeeksType)
      )
      .get()
  }, [date, disableDates])
}
